import { useCallback, useRef, type RefObject } from 'react';

// -- useConversationAutoScroll -------------------------------------------------
// Keeps a conversation's latest message in view as the editor reveals content,
// shared by the student runtime and the admin previews so they scroll the same
// way. Driven by tiptap's onSelectionUpdate (fired as each block is appended).
// Smooth-scrolls to the latest message, but only while the user is already near
// it, so it doesn't yank them back down if they've scrolled away to read.
//
// The student lays its conversation out column-reversed (latest at top:0); the
// previews use a normal column (latest at the bottom). Pass `reversed` to match.
//
// Pass `suppressSnapRef` to temporarily disable the snap-to-latest: when its
// `.current` is `true`, an append won't move the scroll. The student runtime
// uses this while it restores the learner's scroll position across the
// reading-language remount, so the seed repaint can't yank them to the latest.
const NEAR_LATEST_THRESHOLD_PX = 600;

export function useConversationAutoScroll<T extends HTMLElement>({
  reversed = false,
  suppressSnapRef,
}: { reversed?: boolean; suppressSnapRef?: RefObject<boolean> } = {}) {
  const containerRef = useRef<T | null>(null);

  const onSelectionUpdate = useCallback(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    if (suppressSnapRef?.current === true) {
      return;
    }

    if (reversed) {
      // Latest message sits at scrollTop 0; scrollTop grows negative as the user
      // scrolls back through history.
      if (el.scrollTop > -NEAR_LATEST_THRESHOLD_PX) {
        el.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < NEAR_LATEST_THRESHOLD_PX) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [reversed, suppressSnapRef]);

  return { containerRef, onSelectionUpdate };
}
